import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  GlobalStyles,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import axios from "axios";
import { addDays, differenceInCalendarDays, endOfDay, startOfDay } from "date-fns";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import useResponsive from "../../minimals/hooks/useResponsive";
import { getStatusColor } from "../calendar/CalendarUtils";
import { fNumber, fShortenNumber } from "../utils/formatNumber";
import { fMonthDayYearSlashNotation } from "../utils/formatTime";
import DashboardLayoutDrawer, { DashboardSectionId, useDashboardLayout } from "./DashboardLayoutDrawer";
import { MyDashboardData } from "./types";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
// The API returns the next 30 days of closings; the selector narrows that down without refetching
const CLOSINGS_WINDOW_OPTIONS = [7, 14, 30];
// Same order the API checks them in
const ROLE_ORDER = ["Held by", "Sales rep", "Post-closer", "Pro forma", "Funder", "Placed by"];

const formatDate = (date: Date | string | null | undefined) => (date ? fMonthDayYearSlashNotation(date) : "");
const formatMoney = (amount: number) => `$${fShortenNumber(amount).toUpperCase()}`;
// Suffixed orders (204481A, 209984-01) are companions of the base order and repeat its loan amount and sales price
const baseOrderNumber = (number: string) => number.replace(/(-\d+|[A-Z]+)$/i, "");

type SortDirection = "asc" | "desc";

// Items without a date stay at the end in both directions
const sortByDate = <T,>(items: T[], getDate: (item: T) => Date | string | null | undefined, direction: SortDirection) =>
  [...items].sort((a, b) => {
    const aDate = getDate(a);
    const bDate = getDate(b);
    if (!aDate || !bDate) return (aDate ? 0 : 1) - (bDate ? 0 : 1);
    const diff = +new Date(aDate) - +new Date(bDate);
    return direction === "asc" ? diff : -diff;
  });

interface DateSort {
  // The table header that sorts on click
  column: string;
  direction: SortDirection;
  onToggle: () => void;
}

const OrderLink = ({ number }: { number: string | null }) =>
  number ? (
    <Link variant="body2" href={`${momentumUrl}new-home/${number}`} target="_blank" sx={{ whiteSpace: "nowrap" }}>
      {number}
    </Link>
  ) : null;

const StatusChip = ({ status }: { status: string }) => (
  <Chip
    size="small"
    label={status}
    sx={{ color: "#fff", backgroundColor: getStatusColor(status).color ?? "grey.500", fontWeight: 600 }}
  />
);

// Greyed out like a disabled control, for sections with nothing to show
// Outlined rather than filled so it still stands apart from the grey page background
const disabledCardSx = {
  bgcolor: "transparent",
  boxShadow: "none",
  border: "1px dashed",
  borderColor: "divider"
};

const StatCard = ({
  title,
  value,
  subtitle,
  disabled = false
}: {
  title: string;
  value: ReactNode;
  subtitle: string;
  disabled?: boolean;
}) => {
  // Two to a row on phones, so tighter padding and a smaller number
  const isMobile = useResponsive("down", "md");
  return (
    <Card sx={{ height: "100%", ...(disabled && disabledCardSx) }}>
      <CardContent sx={isMobile ? { p: 2, "&:last-child": { pb: 2 } } : undefined}>
        <Typography variant="subtitle2" color={disabled ? "text.disabled" : "text.secondary"}>
          {title}
        </Typography>
        <Typography variant={isMobile ? "h4" : "h3"} color={disabled ? "text.disabled" : undefined}>
          {value}
        </Typography>
        <Typography variant="caption" color={disabled ? "text.disabled" : "text.secondary"}>
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
};

const LoadingRows = () => (
  <Stack spacing={1}>
    {[...Array(4)].map((_, i) => (
      <Skeleton key={i} variant="rounded" height={32} />
    ))}
  </Stack>
);

interface MobileListItem {
  key: string;
  title: ReactNode;
  badge: ReactNode;
  lines: ReactNode[];
}

const MOBILE_LIST_PAGE_SIZE = 10;

// Phone version of a section's table: one stacked row per item, first few shown with a button for the rest
const MobileList = ({ items }: { items: MobileListItem[] }) => {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const visibleItems = isShowingAll ? items : items.slice(0, MOBILE_LIST_PAGE_SIZE);
  return (
    <>
      <Stack divider={<Divider flexItem />}>
        {visibleItems.map((item) => (
          <Box key={item.key} sx={{ py: 1.25 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
              {item.title}
              {item.badge}
            </Stack>
            {item.lines.map(
              (line, i) =>
                line && (
                  <Typography key={i} variant="body2" color={i === 0 ? "text.primary" : "text.secondary"}>
                    {line}
                  </Typography>
                )
            )}
          </Box>
        ))}
      </Stack>
      {items.length > MOBILE_LIST_PAGE_SIZE && (
        <Button fullWidth color="inherit" onClick={() => setIsShowingAll((showing) => !showing)} sx={{ mt: 1 }}>
          {isShowingAll ? "Show fewer" : `Show all ${fNumber(items.length)}`}
        </Button>
      )}
    </>
  );
};

const SectionCard = ({
  title,
  subheader,
  isLoading,
  isEmpty,
  emptyText,
  headers,
  action,
  disabled = false,
  mobileItems,
  dateSort,
  children
}: {
  title: string;
  subheader: string;
  isLoading: boolean;
  isEmpty: boolean;
  emptyText: string;
  headers: string[];
  action?: ReactNode;
  disabled?: boolean;
  // Shown instead of the table on phones, where the table would need sideways scrolling
  mobileItems?: MobileListItem[];
  dateSort?: DateSort;
  children: ReactNode;
}) => {
  const isMobile = useResponsive("down", "md");
  return (
    <Card sx={{ height: "100%", ...(disabled && disabledCardSx) }}>
      <CardHeader
        title={title}
        subheader={subheader}
        action={action}
        titleTypographyProps={disabled ? { color: "text.disabled" } : undefined}
        subheaderTypographyProps={disabled ? { color: "text.disabled" } : undefined}
      />
      <CardContent>
        {isLoading ? (
          <LoadingRows />
        ) : isEmpty ? (
          <Typography variant="body2" color={disabled ? "text.disabled" : "text.secondary"}>
            {emptyText}
          </Typography>
        ) : isMobile && mobileItems ? (
          <>
            {dateSort && (
              <Button
                size="small"
                color="inherit"
                startIcon={<SwapVertIcon />}
                onClick={dateSort.onToggle}
                sx={{ mb: 0.5 }}
              >
                {dateSort.direction === "asc" ? "Oldest first" : "Newest first"}
              </Button>
            )}
            <MobileList items={mobileItems} />
          </>
        ) : (
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header} sortDirection={dateSort?.column === header ? dateSort.direction : false}>
                      {dateSort?.column === header ? (
                        <TableSortLabel active direction={dateSort.direction} onClick={dateSort.onToggle}>
                          {header}
                        </TableSortLabel>
                      ) : (
                        header
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{children}</TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

interface BreakdownRow {
  label: string;
  count: number;
  detail?: string;
}

// Each row's bar is sized against the largest row in the list
const BreakdownList = ({ rows }: { rows: BreakdownRow[] }) => {
  const max = Math.max(...rows.map((row) => row.count), 1);
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => (
        <Box key={row.label}>
          <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
            <Typography variant="body2">{row.label}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.detail ? `${fNumber(row.count)} · ${row.detail}` : fNumber(row.count)}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={(row.count / max) * 100} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      ))}
    </Stack>
  );
};

const BreakdownCard = ({
  title,
  subheader,
  isLoading,
  sections
}: {
  title: string;
  subheader: string;
  isLoading: boolean;
  sections: { title?: string; rows: BreakdownRow[] }[];
}) => {
  const isEmpty = sections.every((section) => !section.rows.length);
  return (
    <Card sx={{ height: "100%", ...(!isLoading && isEmpty && disabledCardSx) }}>
      <CardHeader
        title={title}
        subheader={subheader}
        titleTypographyProps={!isLoading && isEmpty ? { color: "text.disabled" } : undefined}
        subheaderTypographyProps={!isLoading && isEmpty ? { color: "text.disabled" } : undefined}
      />
      <CardContent>
        {isLoading ? (
          <LoadingRows />
        ) : isEmpty ? (
          <Typography variant="body2" color="text.disabled">
            No active orders.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {sections
              .filter((section) => section.rows.length)
              .map((section, i) => (
                <Box key={section.title ?? i}>
                  {section.title && (
                    <Typography variant="overline" color="text.secondary" component="div" sx={{ mb: 1 }}>
                      {section.title}
                    </Typography>
                  )}
                  <BreakdownList rows={section.rows} />
                </Box>
              ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default function MyDashboard() {
  const { user } = useParams();
  const [data, setData] = useState<MyDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [closingsWindowDays, setClosingsWindowDays] = useState(14);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const { layout, setLayout, resetLayout, isDefault } = useDashboardLayout();
  const isMobile = useResponsive("down", "md");
  const [needsAttentionSort, setNeedsAttentionSort] = useState<SortDirection>("desc");
  const [closingsSort, setClosingsSort] = useState<SortDirection>("desc");
  const [ordersSort, setOrdersSort] = useState<SortDirection>("desc");
  const flip = (direction: SortDirection): SortDirection => (direction === "asc" ? "desc" : "asc");

  useEffect(() => {
    const getDashboard = async () => {
      setIsLoading(true);
      setError(null);
      setData(null);
      try {
        const { data } = await axios.get<MyDashboardData>(`/api/dashboard/user/${encodeURIComponent(user ?? "")}`);
        setData(data);
      } catch (e) {
        const status = axios.isAxiosError(e) ? e.response?.status : undefined;
        if (status === 403) {
          setError("You don't have access to user dashboards.");
        } else if (status === 404) {
          setError(`There's no dashboard for "${user}".`);
        } else {
          console.error("Failed to fetch dashboard:", e);
          setError("Couldn't load this dashboard. Try refreshing the page.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    getDashboard();
  }, [user]);

  const orders = useMemo(
    () =>
      [...(data?.orders ?? [])].sort(
        (a, b) =>
          (a.settlementDate ? +new Date(a.settlementDate) : Infinity) -
          (b.settlementDate ? +new Date(b.settlementDate) : Infinity)
      ),
    [data?.orders]
  );
  const activeStatusCounts = useMemo(
    () =>
      Object.entries(
        orders.reduce<Record<string, number>>((counts, order) => {
          counts[order.status] = (counts[order.status] ?? 0) + 1;
          return counts;
        }, {})
      ).sort((a, b) => b[1] - a[1]),
    [orders]
  );

  const sortedOrders = useMemo(
    () => sortByDate(orders, (order) => order.settlementDate, ordersSort),
    [orders, ordersSort]
  );

  // Active orders whose settlement date has passed. Orders with no date yet are usually just unscheduled, so
  // they're only counted, not listed.
  const { needsAttention, noSettlementDateCount } = useMemo(() => {
    const today = startOfDay(new Date());
    return {
      needsAttention: sortByDate(
        orders.filter((order) => order.settlementDate && new Date(order.settlementDate) < today),
        (order) => order.settlementDate,
        needsAttentionSort
      ).map((order) => ({ order, daysOverdue: differenceInCalendarDays(today, new Date(order.settlementDate!)) })),
      noSettlementDateCount: orders.filter((order) => !order.settlementDate).length
    };
  }, [orders, needsAttentionSort]);

  const upcomingClosings = useMemo(() => {
    const from = startOfDay(new Date());
    const to = endOfDay(addDays(from, closingsWindowDays));
    const inWindow = (data?.closings ?? []).filter((closing) => {
      if (!closing.settlementDate) return false;
      const date = new Date(closing.settlementDate);
      return date >= from && date <= to;
    });
    return sortByDate(inWindow, (closing) => closing.settlementDate, closingsSort);
  }, [data?.closings, closingsWindowDays, closingsSort]);

  const roleRows = useMemo(() => {
    const counts: Record<string, number> = {};
    orders.forEach((order) =>
      (data?.orderRoles[order.number] ?? []).forEach((role) => (counts[role] = (counts[role] ?? 0) + 1))
    );
    return ROLE_ORDER.filter((role) => counts[role]).map((role) => ({ label: role, count: counts[role] }));
  }, [orders, data?.orderRoles]);

  // Order counts include companion orders, but each deal's money is only counted once
  const volume = useMemo(() => {
    const sumOncePerDeal = (
      list: typeof orders,
      getAmount: (order: (typeof orders)[number]) => number | null | undefined
    ) => {
      const amounts = new Map<string, number>();
      list.forEach((order) => {
        const base = baseOrderNumber(order.number);
        amounts.set(base, Math.max(amounts.get(base) ?? 0, getAmount(order) ?? 0));
      });
      return [...amounts.values()].reduce((sum, amount) => sum + amount, 0);
    };
    const groupBy = (getKey: (order: (typeof orders)[number]) => string | null | undefined) => {
      const groups: Record<string, typeof orders> = {};
      orders.forEach((order) => (groups[getKey(order) || "Unknown"] ??= []).push(order));
      return Object.entries(groups)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([label, group]) => ({
          label,
          count: group.length,
          detail: `${formatMoney(sumOncePerDeal(group, (order) => order.loanAmount))} loans`
        }));
    };
    return {
      loanAmount: sumOncePerDeal(orders, (order) => order.loanAmount),
      salesPrice: sumOncePerDeal(orders, (order) => order.salesPrice),
      byPropertyType: groupBy((order) => order.propertyType),
      byTransactionType: groupBy((order) => order.transactionType)
    };
  }, [orders]);

  const closingsWindowLabel = `Next ${closingsWindowDays} days`;
  const isClosingsEmpty = !isLoading && !upcomingClosings.length;
  // With nothing in the whole 30 days, switching windows can't show anything either
  const hasAnyClosings = !!data?.closings.length;
  const statValue = (value: ReactNode) => (isLoading ? <Skeleton width={60} /> : value);

  // Each entry is one or more Grid items; the viewer's layout decides which show and in what order
  const sections: Record<DashboardSectionId, ReactNode> = {
    stats: (
      <>
        <Grid item xs={6} sm={6} lg={3}>
          <StatCard title="Active orders" value={statValue(orders.length)} subtitle="Not closed or canceled" />
        </Grid>
        <Grid item xs={6} sm={6} lg={3}>
          <StatCard
            title="Needs attention"
            value={statValue(needsAttention.length)}
            subtitle="Active, settlement date passed"
            disabled={!isLoading && !needsAttention.length}
          />
        </Grid>
        <Grid item xs={6} sm={6} lg={3}>
          <StatCard
            title="Upcoming closings"
            value={statValue(upcomingClosings.length)}
            subtitle={closingsWindowLabel}
            disabled={isClosingsEmpty}
          />
        </Grid>
        <Grid item xs={6} sm={6} lg={3}>
          <StatCard
            title="Active loan volume"
            value={statValue(formatMoney(volume.loanAmount))}
            subtitle={isLoading ? "Loan amounts" : `${formatMoney(volume.salesPrice)} in sales price`}
            disabled={!isLoading && !volume.loanAmount && !volume.salesPrice}
          />
        </Grid>
      </>
    ),
    needsAttention: (
      <>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Needs attention"
            subheader={
              noSettlementDateCount
                ? `Active orders past their settlement date · ${fNumber(noSettlementDateCount)} more have no date set`
                : "Active orders past their settlement date"
            }
            isLoading={isLoading}
            isEmpty={!needsAttention.length}
            emptyText="No active orders are past their settlement date."
            headers={["Settlement", "Order", "Client", "Status", "Overdue"]}
            dateSort={{
              column: "Settlement",
              direction: needsAttentionSort,
              onToggle: () => setNeedsAttentionSort(flip)
            }}
            mobileItems={needsAttention.map(({ order, daysOverdue }) => ({
              key: order.number,
              title: <OrderLink number={order.number} />,
              badge: <StatusChip status={order.status} />,
              lines: [
                order.clientName,
                <>
                  {formatDate(order.settlementDate)} ·{" "}
                  <Box component="span" sx={{ color: "error.main" }}>
                    {fNumber(daysOverdue)} {daysOverdue === 1 ? "day" : "days"} overdue
                  </Box>
                </>
              ]
            }))}
            disabled={!isLoading && !needsAttention.length}
          >
            {needsAttention.map(({ order, daysOverdue }) => (
              <TableRow key={order.number} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(order.settlementDate)}</TableCell>
                <TableCell>
                  <OrderLink number={order.number} />
                </TableCell>
                <TableCell>{order.clientName}</TableCell>
                <TableCell>
                  <StatusChip status={order.status} />
                </TableCell>
                <TableCell sx={{ color: "error.main", whiteSpace: "nowrap" }}>
                  {fNumber(daysOverdue)} {daysOverdue === 1 ? "day" : "days"}
                </TableCell>
              </TableRow>
            ))}
          </SectionCard>
        </Grid>
      </>
    ),
    upcomingClosings: (
      <>
        <Grid item xs={12} lg={6}>
          <SectionCard
            title="Upcoming closings"
            subheader={closingsWindowLabel}
            isLoading={isLoading}
            isEmpty={!upcomingClosings.length}
            emptyText={`No closings in the next ${closingsWindowDays} days.`}
            headers={["Date", "Order", "Client", "State", "Sales rep", "Status"]}
            dateSort={{ column: "Date", direction: closingsSort, onToggle: () => setClosingsSort(flip) }}
            mobileItems={upcomingClosings.map((closing, i) => ({
              key: closing.orderNumber ?? String(i),
              title: <OrderLink number={closing.orderNumber} />,
              badge: <StatusChip status={closing.orderStatus} />,
              lines: [
                closing.clientName,
                [formatDate(closing.settlementDate), closing.state, closing.salesRep].filter(Boolean).join(" · ")
              ]
            }))}
            disabled={isClosingsEmpty}
            action={
              <ToggleButtonGroup
                size="small"
                exclusive
                disabled={!isLoading && !hasAnyClosings}
                value={closingsWindowDays}
                onChange={(_, days: number | null) => days && setClosingsWindowDays(days)}
              >
                {CLOSINGS_WINDOW_OPTIONS.map((days) => (
                  <ToggleButton key={days} value={days} sx={{ px: isMobile ? 1 : 1.5 }}>
                    {days}d
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            }
          >
            {upcomingClosings.map((closing) => (
              <TableRow key={closing.orderNumber} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(closing.settlementDate)}</TableCell>
                <TableCell>
                  <OrderLink number={closing.orderNumber} />
                </TableCell>
                <TableCell>{closing.clientName}</TableCell>
                <TableCell>{closing.state}</TableCell>
                <TableCell>{closing.salesRep}</TableCell>
                <TableCell>
                  <StatusChip status={closing.orderStatus} />
                </TableCell>
              </TableRow>
            ))}
          </SectionCard>
        </Grid>
      </>
    ),
    role: (
      <>
        <Grid item xs={12} md={6}>
          <BreakdownCard
            title="Your role"
            subheader="Active orders by how you're on them (an order can count twice)"
            isLoading={isLoading}
            sections={[{ rows: roleRows }]}
          />
        </Grid>
      </>
    ),
    volume: (
      <>
        <Grid item xs={12} md={6}>
          <BreakdownCard
            title="Volume"
            subheader="Active orders and their loan amounts"
            isLoading={isLoading}
            sections={[
              { title: "Property type", rows: volume.byPropertyType },
              { title: "Transaction type", rows: volume.byTransactionType }
            ]}
          />
        </Grid>
      </>
    ),
    activeOrders: (
      <>
        <Grid item xs={12}>
          <SectionCard
            title="Active orders"
            subheader="Sorted by settlement date"
            isLoading={isLoading}
            isEmpty={!orders.length}
            emptyText="No active orders."
            headers={["Settlement", "Order", "Client", "Property", "Held by", "Sales rep", "Your role", "Status"]}
            dateSort={{ column: "Settlement", direction: ordersSort, onToggle: () => setOrdersSort(flip) }}
            mobileItems={sortedOrders.map((order) => ({
              key: order.number,
              title: <OrderLink number={order.number} />,
              badge: <StatusChip status={order.status} />,
              lines: [
                order.clientName,
                order.propertyAddress,
                [
                  order.settlementDate ? `Settles ${formatDate(order.settlementDate)}` : "No settlement date",
                  (data?.orderRoles[order.number] ?? []).join(", ")
                ]
                  .filter(Boolean)
                  .join(" · ")
              ].filter(Boolean)
            }))}
            disabled={!isLoading && !orders.length}
          >
            {sortedOrders.map((order) => (
              <TableRow key={order.number} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(order.settlementDate)}</TableCell>
                <TableCell>
                  <OrderLink number={order.number} />
                </TableCell>
                <TableCell>{order.clientName}</TableCell>
                <TableCell>{order.propertyAddress}</TableCell>
                <TableCell>{order.heldByFullName}</TableCell>
                <TableCell>{order.salesRepFullName}</TableCell>
                <TableCell>{(data?.orderRoles[order.number] ?? []).join(", ")}</TableCell>
                <TableCell>
                  <StatusChip status={order.status} />
                </TableCell>
              </TableRow>
            ))}
          </SectionCard>
          {!isLoading && !!activeStatusCounts.length && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {activeStatusCounts.map(([status, count]) => (
                <Chip key={status} variant="outlined" label={`${status}: ${count}`} />
              ))}
            </Box>
          )}
        </Grid>
      </>
    )
  };
  const visibleSectionIds = layout.order.filter((id) => !layout.hidden.includes(id));

  return (
    <>
      <Helmet>
        <title>My Dashboard | TitleQ</title>
      </Helmet>

      {/* The desktop layout reserves 172px above main; this page needs less */}
      {/* Grey page behind white cards so the cards stand out (dark mode already has that contrast) */}
      <GlobalStyles
        styles={(theme) => ({
          [theme.breakpoints.up("lg")]: { main: { paddingTop: "90px !important" } },
          ...(theme.palette.mode === "light" && { body: { backgroundColor: theme.palette.background.neutral } })
        })}
      />

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Stack spacing={0.5}>
            <Typography variant="h4">{data?.name ? `${data.name}'s Dashboard` : "My Dashboard"}</Typography>
            {!!data?.departments.length && (
              <Typography variant="body2" color="text.secondary">
                {data.departments.join(", ")}
              </Typography>
            )}
          </Stack>
          {!error && (
            <Tooltip title="Customize dashboard">
              <IconButton onClick={() => setIsCustomizeOpen(true)} aria-label="Customize dashboard">
                <SettingsOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <DashboardLayoutDrawer
          open={isCustomizeOpen}
          onClose={() => setIsCustomizeOpen(false)}
          layout={layout}
          onChange={setLayout}
          onReset={resetLayout}
          isDefault={isDefault}
        />

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {visibleSectionIds.length ? (
              visibleSectionIds.map((id) => <Fragment key={id}>{sections[id]}</Fragment>)
            ) : (
              <Grid item xs={12}>
                <Alert severity="info">All sections are hidden. Use the gear icon to show them again.</Alert>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </>
  );
}
